/**
 * Chapter 3 Cue Choice Component - Bravery Badge (Courage)
 *
 * Trauma-informed courage activity offering two approaches:
 * Option 1: "I Can Try!" — chant, take a tiny step, say one small action (original exercise)
 * Option 2: "Courage Check-In" — grounding technique (5-4-3-2-1 sensory anchor)
 *            that calms the nervous system before facing something scary, with no
 *            performance or action required
 *
 * Based on the same dual-path architecture as BreathingCueChoice (Chapter 1).
 */

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { X, Zap, Anchor, Info, Shield } from "lucide-react";

interface Chapter3CueChoiceProps {
  onComplete: () => void;
  isCompleted: boolean;
  trackButton?: (buttonId: string, metadata?: any) => void;
}

/** Shows one of the two activity modes step-by-step */
function ActivityMode({ mode, onBack, onComplete }: { mode: 'try' | 'ground'; onBack: () => void; onComplete: () => void }) {
  const [currentStep, setCurrentStep] = useState(0);

  const trySteps = [
    "Take a slow breath to get ready.",
    "Chant together: \"I can try!\"",
    "Take one tiny step forward — just a gentle foot tap.",
    "Say: \"I'll try [one small, specific thing].\"",
    "That one step IS bravery. Well done."
  ];

  const groundSteps = [
    "Let's anchor your brave body.",
    "Look around: find 5 things you can SEE.",
    "Now find 4 things you can TOUCH — feel their texture.",
    "Listen carefully: 3 things you can HEAR right now.",
    "Notice 2 things you can SMELL — and 1 thing you CAN DO when ready."
  ];

  const steps = mode === 'try' ? trySteps : groundSteps;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-lg font-semibold text-blue-800">
          {mode === 'try' ? (
            <><Zap className="w-5 h-5 text-yellow-500" /> I Can Try!</>
          ) : (
            <><Anchor className="w-5 h-5 text-blue-500" /> Courage Check-In</>
          )}
        </div>
      </div>

      <div className="bg-blue-50 rounded-lg p-6 min-h-[120px] flex items-center justify-center border-2 border-blue-200">
        <AnimatePresence mode="wait">
          <motion.p
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-xl text-gray-800 text-center leading-relaxed"
          >
            {steps[currentStep]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Animated visual */}
      <div className="flex justify-center">
        <motion.div
          className={`w-20 h-20 rounded-full ${
            mode === 'try'
              ? 'bg-gradient-to-br from-yellow-400 to-orange-400'
              : 'bg-gradient-to-br from-blue-400 to-indigo-500'
          }`}
          animate={mode === 'try'
            ? { scale: [1, 1.2, 1], rotate: [0, 5, -5, 0] }
            : { scale: [1, 1.05, 1] }
          }
          transition={{ duration: mode === 'try' ? 1.5 : 4, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2">
        {steps.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentStep(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentStep ? 'bg-blue-600 w-6' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      <div className="flex justify-between items-center pt-2">
        <Button variant="ghost" onClick={onBack} className="text-gray-600 hover:text-gray-800">
          ← Back to choices
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.max(0, p - 1))} disabled={currentStep === 0} className="text-sm">Previous</Button>
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.min(steps.length - 1, p + 1))} disabled={currentStep === steps.length - 1} className="text-sm">Next</Button>
        </div>
      </div>

      {currentStep === steps.length - 1 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-2">
          <Button onClick={onComplete} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-base font-medium">
            {mode === 'try' ? 'I tried!' : 'I anchored myself!'}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}

/** Parent/Educator info panel */
function EducatorInfo({ onClose }: { onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 space-y-3"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">For Parents & Educators</h4>
        </div>
        <button onClick={onClose} className="text-blue-400 hover:text-blue-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="text-sm text-blue-800 space-y-2">
        <p><strong>Why two approaches to courage?</strong></p>
        <p>For some children, anxiety about a task is so high that asking them to "take a step" immediately can feel overwhelming or shaming if they can't do it.</p>
        <p><strong>"Courage Check-In"</strong> uses the evidence-based 5-4-3-2-1 grounding technique, which activates the parasympathetic nervous system before any action is attempted. It communicates: "Your safety comes first." From a regulated state, courageous action becomes possible naturally.</p>
        <p>Both paths build courage—one through action, one through regulation first.</p>
      </div>
    </motion.div>
  );
}

/** Main Chapter 3 Cue Choice Component */
export function Chapter3CueChoice({ onComplete, isCompleted, trackButton }: Chapter3CueChoiceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'try' | 'ground' | null>(null);
  const [showEducatorInfo, setShowEducatorInfo] = useState(false);

  const handleClose = () => {
    setSelectedMode(null);
    setShowEducatorInfo(false);
    setIsOpen(false);
  };

  const handleComplete = () => {
    onComplete();
    handleClose();
  };

  return (
    <>
      {/* Glowing trigger button */}
      <motion.button
        className="relative bg-blue-100 p-8 rounded-full border-4 border-white/70 shadow-2xl backdrop-blur-sm w-24 h-24 flex items-center justify-center"
        onClick={() => {
          trackButton?.('bravery_badge_cue_open', { cue_name: 'Bravery Badge' });
          setIsOpen(true);
        }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          scale: [1, 1.25, 1],
          boxShadow: [
            "0 0 0 0 rgba(59, 130, 246, 0.7)",
            "0 0 0 30px rgba(59, 130, 246, 0)",
            "0 0 0 0 rgba(59, 130, 246, 0.7)",
          ],
        }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <Shield className="w-12 h-12 text-blue-600" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-200 relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 border-2 border-current flex items-center justify-center">
                    <Shield className="w-5 h-5" />
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={handleClose} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
                <div className="text-center px-16">
                  <h3 className="text-xl font-bold">
                    <span className="font-serif font-bold bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-600 bg-clip-text text-transparent">Brave Bridge Step</span>
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {showEducatorInfo && <EducatorInfo onClose={() => setShowEducatorInfo(false)} />}

                  {!selectedMode && !showEducatorInfo && (
                    <motion.div key="choice" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                      <p className="text-center text-gray-700 mb-2">Choose the path that feels right in this moment:</p>
                      <button onClick={() => setShowEducatorInfo(true)} className="flex items-center gap-1 mx-auto text-sm text-blue-600 hover:text-blue-800 mb-4">
                        <Info className="w-4 h-4" /> For Parents/Educators
                      </button>

                      {/* Option 1 */}
                      <motion.button
                        onClick={() => { trackButton?.('bravery_path_try', { cue_name: 'Bravery Badge' }); setSelectedMode('try'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl text-left hover:border-yellow-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0">
                            <Zap className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-yellow-900 mb-1">I Can Try!</h4>
                            <p className="text-sm text-yellow-700">Chant, take one tiny step, and say one small brave action</p>
                          </div>
                        </div>
                      </motion.button>

                      {/* Option 2 */}
                      <motion.button
                        onClick={() => { trackButton?.('bravery_path_ground', { cue_name: 'Bravery Badge' }); setSelectedMode('ground'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl text-left hover:border-blue-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                            <Anchor className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-900 mb-1">Courage Check-In</h4>
                            <p className="text-sm text-blue-700">Use your senses to ground yourself first — see, touch, hear, smell — no action required yet</p>
                          </div>
                        </div>
                      </motion.button>

                      <p className="text-center text-sm text-gray-500 mt-4">Both paths lead to bravery — choose what feels safe right now.</p>
                    </motion.div>
                  )}

                  {selectedMode && !showEducatorInfo && (
                    <ActivityMode key={selectedMode} mode={selectedMode} onBack={() => setSelectedMode(null)} onComplete={handleComplete} />
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
